/**
 * TaskDrawer — panneau latéral : fiche d'une tâche (dossier Master, expertise, type, priorité, statut, échéance) + étapes à cocher.
 * Les étapes d'une tâche existante sont enregistrées immédiatement ; les autres champs à « Enregistrer ».
 */
import { useEffect, useRef, useState } from "react";
import { BellRing, CheckCircle2, CheckSquare, Circle, Copy, GripVertical, Plus, Repeat, RotateCcw, Save, Square, Trash2, X } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { FISCAL_YEARS } from "@/constants/modules";
import { Btn, Field, Pill, errMsg, inputCls } from "../comptabilite/_shared";
import { ALERT_OPTIONS, EXPERTISES, MASTER_EXPERTISE, RECURRENCE_OPTIONS, RECURRENCE_UNITS, TASK_PRIORITIES, TASK_STATUSES, TASK_TYPES, fmtDateTime, masterLabel, meta } from "./taskHelpers";

const EMPTY = { title: "", description: "", master_id: "", expertise: "cabinet", task_type: "tache", priority: "normale", status: "a_faire", tax_year: "", due_date: "", due_time: "", rec_freq: "", rec_every: 1, rec_unit: "days", alerts: [] };
const uid = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

function StepRow({ s, onToggle, onRename, onRemove }) {
  const [label, setLabel] = useState(s.label);
  useEffect(() => setLabel(s.label), [s.label]);
  return (
    <div className={`group flex items-center gap-2 px-2 py-1.5 rounded-lg border transition ${s.done ? "bg-emerald-50/60 border-emerald-100" : "bg-white border-slate-200 hover:border-emerald-300"}`} data-testid={`step-row-${s.id}`}>
      <GripVertical className="w-3.5 h-3.5 text-slate-200 shrink-0" />
      <button onClick={onToggle} className={`shrink-0 ${s.done ? "text-emerald-600" : "text-slate-300 hover:text-emerald-600"}`} data-testid={`step-toggle-${s.id}`}>
        {s.done ? <CheckCircle2 className="w-5 h-5" /> : <Circle className="w-5 h-5" />}
      </button>
      <input value={label} onChange={(e) => setLabel(e.target.value)} onBlur={() => label.trim() && label !== s.label ? onRename(label.trim()) : setLabel(s.label)}
        onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} maxLength={300}
        className={`flex-1 bg-transparent text-sm focus:outline-none ${s.done ? "line-through text-slate-500" : "text-slate-800"}`} data-testid={`step-label-${s.id}`} />
      {s.done && s.done_at && <span className="text-[10px] text-emerald-700 shrink-0">{new Date(s.done_at).toLocaleDateString("fr-CA")}</span>}
      <button onClick={onRemove} className="p-1 text-slate-300 hover:text-rose-600 opacity-0 group-hover:opacity-100 transition" title="Retirer l'étape" data-testid={`step-remove-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
    </div>
  );
}

export default function TaskDrawer({ task, masters, defaultAlerts = [], onClose, onChanged }) {
  const isNew = !task?.id;
  const [f, setF] = useState({
    ...EMPTY, ...(task || {}), master_id: task?.master_id || "", tax_year: task?.tax_year || "", due_date: task?.due_date || "", due_time: task?.due_time || "",
    rec_freq: task?.recurrence?.freq || "", rec_every: task?.recurrence?.every || 1, rec_unit: task?.recurrence?.unit || "days",
    alerts: task?.id ? (task.alerts || []) : defaultAlerts,
  });
  const [steps, setSteps] = useState(task?.steps || []);
  const [newStep, setNewStep] = useState("");
  const [busy, setBusy] = useState(false);
  const alertsTouched = useRef(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => { if (isNew && !alertsTouched.current) setF((p) => ({ ...p, alerts: defaultAlerts })); }, [defaultAlerts, isNew]);

  useEffect(() => {
    const h = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const pickMaster = (id) => {
    set("master_id", id);
    const m = masters.find((x) => x.id === id);
    const keys = (m?.active_expertises || []).map((k) => MASTER_EXPERTISE[k]).filter(Boolean);
    if (isNew && keys.length === 1) set("expertise", keys[0]);
  };

  const payload = () => ({
    title: f.title.trim(), description: f.description || "", master_id: f.master_id || null, expertise: f.expertise, task_type: f.task_type, priority: f.priority, status: f.status,
    tax_year: f.tax_year ? Number(f.tax_year) : null, due_date: f.due_date || null, due_time: f.due_time || null,
    recurrence: f.rec_freq ? { freq: f.rec_freq, every: Number(f.rec_every) || 1, unit: f.rec_unit } : null,
    alerts: f.due_date ? f.alerts : [],
  });
  const toggleAlert = (id) => { alertsTouched.current = true; set("alerts", f.alerts.includes(id) ? f.alerts.filter((a) => a !== id) : [...f.alerts, id]); };

  const save = async () => {
    if (!f.title.trim()) return toast.error("Titre requis");
    setBusy(true);
    try {
      if (isNew) {
        const r = await api.post("/tasks", { ...payload(), steps: steps.map((s) => ({ label: s.label, done: s.done })) });
        toast.success("Tâche créée");
        onChanged(r.data, true);
      } else {
        const r = await api.put(`/tasks/${task.id}`, { ...payload(), clear_master: !f.master_id, clear_due: !f.due_date, clear_due_time: !f.due_time, clear_recurrence: !f.rec_freq });
        toast.success("Tâche enregistrée");
        onChanged(r.data, true);
      }
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const persistSteps = async (next) => {
    setSteps(next);
    if (isNew) return;
    try { const r = await api.put(`/tasks/${task.id}`, { steps: next }); setSteps(r.data.steps); onChanged(r.data, false); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const toggleStep = async (s) => {
    if (isNew) return setSteps(steps.map((x) => (x.id === s.id ? { ...x, done: !x.done } : x)));
    setSteps(steps.map((x) => (x.id === s.id ? { ...x, done: !x.done } : x)));
    try { const r = await api.post(`/tasks/${task.id}/steps/${s.id}/toggle`); setSteps(r.data.steps); onChanged(r.data, false); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const addStep = () => {
    const label = newStep.trim();
    if (!label) return;
    setNewStep("");
    persistSteps([...steps, { id: uid(), label, done: false, done_at: null }]);
  };

  const setAll = (done) => persistSteps(steps.map((s) => ({ ...s, done, done_at: done ? s.done_at || new Date().toISOString() : null })));

  const setStatus = async (status) => {
    if (isNew) return set("status", status);
    setBusy(true);
    try {
      const r = await api.post(`/tasks/${task.id}/status`, { status });
      if (r.data.rolled) { toast.success(`Occurrence terminée — prochaine échéance le ${r.data.due_date}`); onChanged(r.data, true); return; }
      set("status", r.data.status); onChanged(r.data, false); toast.success(status === "terminee" ? "Tâche terminée" : "Tâche réouverte");
    }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const duplicate = async () => {
    setBusy(true);
    try { const r = await api.post(`/tasks/${task.id}/duplicate`); toast.success("Copie créée — étapes remises à zéro"); onChanged(r.data, true); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Supprimer « ${task.title} » ?`)) return;
    setBusy(true);
    try { await api.delete(`/tasks/${task.id}`); toast.success("Tâche supprimée"); onChanged(null, true); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const done = steps.filter((s) => s.done).length;
  const pct = steps.length ? Math.round((100 * done) / steps.length) : 0;
  const type = meta(TASK_TYPES, f.task_type);
  const st = meta(TASK_STATUSES, f.status);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose} data-testid="task-drawer">
      <aside className="w-full max-w-xl h-full bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-200" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center shrink-0"><type.icon className="w-5 h-5" /></div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-slate-900 truncate" data-testid="task-drawer-title">{isNew ? "Nouvelle tâche" : task.title}</h3>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <Pill cls={st.cls} testId="task-drawer-status-pill">{st.label}</Pill>
              {!isNew && task.master_name && <span className="text-xs text-slate-500 truncate">{task.master_name}</span>}
            </div>
          </div>
          {!isNew && (
            f.status === "terminee"
              ? <Btn tone="slate" icon={RotateCcw} onClick={() => setStatus("a_faire")} busy={busy} testId="task-reopen-btn">Réouvrir</Btn>
              : <Btn icon={CheckCircle2} onClick={() => setStatus("terminee")} busy={busy} testId="task-complete-btn">Terminer</Btn>
          )}
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1" data-testid="task-drawer-close"><X className="w-4 h-4" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          <Field label="Titre *"><input value={f.title} onChange={(e) => set("title", e.target.value)} className={inputCls} maxLength={200} autoFocus={isNew} placeholder="Ex. : Préparer les T4 / RL-1 — 2025" data-testid="task-title-input" /></Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Dossier Master" hint="Le dossier concerné (facultatif pour une tâche interne).">
              <select value={f.master_id} onChange={(e) => pickMaster(e.target.value)} className={inputCls} data-testid="task-master-select">
                <option value="">— Sans dossier (interne) —</option>
                {masters.map((m) => <option key={m.id} value={m.id}>{masterLabel(m)}</option>)}
              </select>
            </Field>
            <Field label="Expertise">
              <select value={f.expertise} onChange={(e) => set("expertise", e.target.value)} className={inputCls} data-testid="task-expertise-select">
                {EXPERTISES.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Type">
              <select value={f.task_type} onChange={(e) => set("task_type", e.target.value)} className={inputCls} data-testid="task-type-select">
                {TASK_TYPES.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
              </select>
            </Field>
            <Field label="Priorité">
              <select value={f.priority} onChange={(e) => set("priority", e.target.value)} className={inputCls} data-testid="task-priority-select">
                {TASK_PRIORITIES.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
              </select>
            </Field>
            <Field label="Statut">
              <select value={f.status} onChange={(e) => set("status", e.target.value)} className={inputCls} data-testid="task-status-select">
                {TASK_STATUSES.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Année fiscale">
              <select value={f.tax_year} onChange={(e) => set("tax_year", e.target.value)} className={inputCls} data-testid="task-year-select">
                <option value="">—</option>
                {FISCAL_YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </Field>
            <Field label="Échéance">
              <div className="flex gap-2">
                <input type="date" value={f.due_date} onChange={(e) => set("due_date", e.target.value)} className={inputCls} data-testid="task-due-input" />
                <input type="time" value={f.due_time} onChange={(e) => set("due_time", e.target.value)} disabled={!f.due_date} className={`${inputCls} w-[110px] shrink-0`} title="Heure d'échéance (requise pour le rappel « 1 heure avant »)" data-testid="task-due-time-input" />
              </div>
            </Field>
          </div>

          <div className="border rounded-xl p-3 space-y-3 bg-slate-50/60" data-testid="task-schedule">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label={<span className="inline-flex items-center gap-1"><Repeat className="w-3.5 h-3.5" /> Récurrence</span>} hint={f.rec_freq ? "À la complétion : échéance avancée d'une période, étapes décochées ; l'occurrence terminée reste dans « Terminées »." : undefined}>
                <select value={f.rec_freq} onChange={(e) => set("rec_freq", e.target.value)} disabled={!f.due_date} className={inputCls} data-testid="task-recurrence-select">
                  {RECURRENCE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                {f.rec_freq === "custom" && (
                  <div className="flex items-center gap-2 mt-2 text-sm">
                    <span className="text-slate-600">Tous les</span>
                    <input type="number" min={1} max={365} value={f.rec_every} onChange={(e) => set("rec_every", e.target.value)} className={`${inputCls} w-20`} data-testid="task-recurrence-every" />
                    <select value={f.rec_unit} onChange={(e) => set("rec_unit", e.target.value)} className={inputCls} data-testid="task-recurrence-unit">
                      {RECURRENCE_UNITS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                )}
                {!f.due_date && <p className="text-[11px] text-slate-400 mt-1">Choisissez d'abord une échéance.</p>}
              </Field>
              <Field label={<span className="inline-flex items-center gap-1"><BellRing className="w-3.5 h-3.5" /> Rappels avant l'échéance</span>} hint={!f.due_date ? "Choisissez d'abord une échéance." : (f.alerts.includes(60) && !f.due_time ? "« 1 heure avant » : ajoutez une heure d'échéance (sinon 9 h 00 est utilisé)." : "Canaux (courriel · push · cloche) : Paramètres → Notifications.")}>
                <div className="flex flex-wrap gap-1.5" data-testid="task-alerts">
                  {ALERT_OPTIONS.map((a) => {
                    const on = f.alerts.includes(a.id);
                    return (
                      <button key={a.id} type="button" onClick={() => toggleAlert(a.id)} disabled={!f.due_date}
                        className={`px-2.5 py-1 rounded-full text-xs border transition disabled:opacity-40 ${on ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-slate-200 text-slate-600 hover:border-emerald-400"}`}
                        data-testid={`task-alert-${a.id}`}>{a.label}</button>
                    );
                  })}
                </div>
                {!isNew && task.next_alert_at && f.status !== "terminee" && <p className="text-[11px] text-emerald-700 mt-1.5" data-testid="task-next-alert">Prochain rappel : {fmtDateTime(task.next_alert_at)}</p>}
              </Field>
            </div>
          </div>
          <Field label="Description"><textarea value={f.description} onChange={(e) => set("description", e.target.value)} className={`${inputCls} min-h-[72px]`} maxLength={4000} placeholder="Contexte, consignes, documents attendus…" data-testid="task-description-input" /></Field>

          <div className="border rounded-xl overflow-hidden" data-testid="task-steps">
            <div className="px-3 py-2 bg-slate-50 border-b flex items-center gap-3">
              <span className="text-xs font-bold uppercase tracking-wide text-slate-700">Étapes à cocher</span>
              <span className="text-[11px] text-slate-500 tabular-nums" data-testid="task-steps-progress">{steps.length ? `${done}/${steps.length} · ${pct} %` : "Aucune étape"}</span>
              <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden max-w-[140px]"><div className={`h-full transition-all ${pct === 100 && steps.length ? "bg-emerald-500" : "bg-sky-500"}`} style={{ width: `${pct}%` }} /></div>
              {steps.length > 0 && (
                <div className="ml-auto flex gap-1">
                  <button onClick={() => setAll(true)} className="p-1 text-slate-400 hover:text-emerald-700" title="Tout cocher" data-testid="task-steps-check-all"><CheckSquare className="w-4 h-4" /></button>
                  <button onClick={() => setAll(false)} className="p-1 text-slate-400 hover:text-slate-700" title="Tout décocher" data-testid="task-steps-uncheck-all"><Square className="w-4 h-4" /></button>
                </div>
              )}
            </div>
            <div className="p-2 space-y-1">
              {steps.map((s) => (
                <StepRow key={s.id} s={s} onToggle={() => toggleStep(s)}
                  onRename={(label) => persistSteps(steps.map((x) => (x.id === s.id ? { ...x, label } : x)))}
                  onRemove={() => persistSteps(steps.filter((x) => x.id !== s.id))} />
              ))}
              <div className="flex items-center gap-2 pt-1">
                <Plus className="w-4 h-4 text-slate-300 ml-2" />
                <input value={newStep} onChange={(e) => setNewStep(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addStep()} placeholder="Ajouter une étape puis Entrée…" maxLength={300}
                  className="flex-1 text-sm py-1.5 bg-transparent focus:outline-none placeholder:text-slate-400" data-testid="task-step-input" />
                <Btn tone="slate" onClick={addStep} disabled={!newStep.trim()} testId="task-step-add-btn">Ajouter</Btn>
              </div>
            </div>
          </div>
          {pct === 100 && steps.length > 0 && f.status !== "terminee" && !isNew && (
            <div className="flex items-center justify-between gap-2 text-sm text-emerald-900 bg-emerald-50 border border-emerald-200 rounded-xl p-3" data-testid="task-all-steps-banner">
              <span>Toutes les étapes sont cochées.</span>
              <Btn icon={CheckCircle2} onClick={() => setStatus("terminee")} busy={busy} testId="task-all-steps-complete-btn">Marquer terminée</Btn>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 p-4 border-t bg-slate-50">
          {!isNew && <Btn tone="danger" icon={Trash2} onClick={remove} busy={busy} testId="task-delete-btn">Supprimer</Btn>}
          {!isNew && <Btn tone="slate" icon={Copy} onClick={duplicate} busy={busy} title="Créer une copie avec les mêmes étapes (non cochées)" testId="task-duplicate-btn">Dupliquer</Btn>}
          <div className="ml-auto flex gap-2">
            <Btn tone="slate" onClick={onClose} testId="task-cancel-btn">Annuler</Btn>
            <Btn icon={Save} onClick={save} busy={busy} testId="task-save-btn">{isNew ? "Créer" : "Enregistrer"}</Btn>
          </div>
        </div>
      </aside>
    </div>
  );
}
